import { useState, useRef } from 'react';
import { useHubSpotSearch } from '../hooks/useHubSpotSearch';
import type { HubSpotContact } from '@aigftpwatch/shared';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface HubSpotContactSearchProps {
  onSelect: (contact: HubSpotContact) => void;
  selectedContactId?: string | null;
  onClear?: () => void;
}

export function HubSpotContactSearch({ onSelect, selectedContactId, onClear }: HubSpotContactSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isFetching } = useHubSpotSearch(query);

  const contacts = data?.contacts ?? [];

  function handleSelect(contact: HubSpotContact) {
    onSelect(contact);
    setQuery('');
    setOpen(false);
  }

  return (
    <div className="relative">
      <label className="mb-1 block text-sm font-medium text-silver-700">
        Search HubSpot Contacts
      </label>

      {selectedContactId ? (
        <div className="flex items-center gap-2 rounded-lg border border-gold-300 bg-gold-50 px-3 py-2">
          <span className="text-sm text-gold-800">
            HubSpot contact linked (ID: {selectedContactId})
          </span>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="ml-auto text-xs text-gold-600 underline hover:text-gold-800"
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-silver-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                // Delay close to allow click on dropdown items
                setTimeout(() => setOpen(false), 200);
              }}
              placeholder="Search by name, email, or company..."
              className="w-full rounded-lg border border-silver-300 py-2 pl-9 pr-8 text-sm
                         placeholder:text-silver-400 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
            />
            {isFetching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gold-500 border-t-transparent" />
              </div>
            )}
          </div>

          {open && query.trim().length >= 2 && (
            <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-silver-200 bg-white shadow-lg">
              {contacts.length === 0 && !isFetching && (
                <div className="px-4 py-3 text-sm text-silver-500">No contacts found</div>
              )}
              {contacts.map((contact) => {
                const name = [contact.properties.firstname, contact.properties.lastname]
                  .filter(Boolean)
                  .join(' ') || '(No name)';

                return (
                  <button
                    key={contact.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(contact)}
                    className="flex w-full flex-col gap-0.5 border-b border-silver-100 px-4 py-2.5
                               text-left hover:bg-gold-50 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-silver-900">{name}</span>
                      {contact.properties.company && (
                        <span className="text-xs text-silver-500">
                          &middot; {contact.properties.company}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-silver-500">
                      {contact.properties.email && <span>{contact.properties.email}</span>}
                      {contact.properties.phone && <span>{contact.properties.phone}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
